/**
 * StockHistoryDialog.jsx — derived stock-level timeline for a variant.
 *
 * The series is reconstructed server-side from receipts / orders / cycle
 * counts (no new collection). This component renders:
 *   - a recharts LineChart of qty over time
 *   - a small summary header (current / min / max / events in window)
 *   - a table of the underlying events (newest first)
 *
 * Props:
 *   - variant   ({ id, barcode, ... }) — null/undefined hides the dialog
 *   - sku       ({ sku_code }) — for the header label
 *   - open      (boolean)
 *   - onOpenChange (fn)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ArrowUp, ArrowDown, ChartLineUp } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getVariantStockHistory } from '../lib/api';

const REASON_LABEL = {
  receipt: 'Receipt',
  order: 'Order shipped',
  cycle_count: 'Cycle count',
};

const REASON_COLOUR = {
  receipt: 'bg-emerald-100 text-emerald-800',
  order: 'bg-rose-100 text-rose-800',
  cycle_count: 'bg-amber-100 text-amber-800',
};

const WINDOW_OPTIONS = [
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 180, label: '180d' },
  { value: 365, label: '365d' },
];

export default function StockHistoryDialog({ variant, sku, open, onOpenChange }) {
  const [data, setData] = useState(null); // null = loading, {points, current, events}
  const [error, setError] = useState(null);
  const [days, setDays] = useState(90);

  useEffect(() => {
    if (!open || !variant?.id) return;
    let cancelled = false;
    setData(null); setError(null);
    getVariantStockHistory(variant.id, days)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load history'); });
    return () => { cancelled = true; };
  }, [open, variant?.id, days]);

  const stats = useMemo(() => {
    if (!data?.points?.length) return null;
    const qtys = data.points.map((p) => p.qty);
    const first = qtys[0];
    const last = qtys[qtys.length - 1];
    return {
      first, last, min: Math.min(...qtys), max: Math.max(...qtys),
      delta: last - first,
    };
  }, [data]);

  // Reverse events list — surface newest first in the table for parity with
  // the price-history view; keep `points` as-is for the chart so it draws
  // left-to-right oldest-to-newest.
  const eventRows = useMemo(() => {
    if (!data?.points) return [];
    return data.points.filter((p) => p.delta !== undefined).slice().reverse();
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="stock-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <ChartLineUp size={18} weight="fill" />
            Stock history —
            <span className="font-mono text-brand">{sku?.sku_code || variant?.barcode || 'Variant'}</span>
            {(variant?.size || variant?.color) && (
              <span className="text-xs text-muted-foreground font-normal">
                {[variant.size, variant.color].filter(Boolean).join(' · ')}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          {/* Window selector */}
          <div className="flex items-center gap-1 mb-3" data-testid="stock-history-window">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mr-1">Window</span>
            {WINDOW_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setDays(o.value)}
                data-testid={`stock-history-window-${o.value}`}
                className={`px-2 py-1 text-[11px] font-bold uppercase tracking-wider border transition rounded ${
                  days === o.value ? 'bg-foreground text-background border-foreground' : 'bg-card text-muted-foreground border-border hover:border-brand'
                }`}
              >{o.label}</button>
            ))}
          </div>

          {data === null && !error && (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
          )}
          {error && <div className="text-sm text-rose-600 py-4">{error}</div>}
          {data && data.points.length <= 1 && data.events === 0 && (
            <div className="text-sm text-muted-foreground py-8 text-center" data-testid="stock-history-empty">
              No stock movements in the last {days}d. Current on-hand:{' '}
              <span className="font-mono font-bold text-foreground">{data.current}</span>
            </div>
          )}
          {data && data.points.length > 1 && (
            <>
              <div className="border border-border rounded-md p-3 mb-3 bg-card" data-testid="stock-history-chart">
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                  <div className="flex items-baseline gap-3 text-xs">
                    <span className="text-muted-foreground uppercase tracking-wider font-bold">Trend</span>
                    {stats && (
                      <>
                        <span className="text-muted-foreground">Start <span className="font-mono font-bold text-foreground">{stats.first}</span></span>
                        <span className="text-muted-foreground">Now <span className="font-mono font-bold text-foreground">{stats.last}</span></span>
                        <span className={`flex items-center gap-1 font-mono font-bold ${stats.delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {stats.delta >= 0 ? <ArrowUp size={11} weight="bold" /> : <ArrowDown size={11} weight="bold" />}
                          {stats.delta >= 0 ? '+' : ''}{stats.delta}
                        </span>
                      </>
                    )}
                  </div>
                  {stats && (
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Range <span className="font-mono text-foreground">{stats.min} – {stats.max}</span>
                      <span className="ml-3">{data.events} event{data.events === 1 ? '' : 's'}</span>
                    </div>
                  )}
                </div>
                <div style={{ width: '100%', height: 160 }}>
                  <ResponsiveContainer>
                    <LineChart data={data.points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.08)" />
                      <XAxis
                        dataKey="ts"
                        tickFormatter={(v) => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                        tick={{ fontSize: 10, fill: 'currentColor', opacity: 0.7 }}
                        stroke="rgba(0,0,0,.2)"
                        minTickGap={28}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: 'currentColor', opacity: 0.7 }}
                        stroke="rgba(0,0,0,.2)"
                        domain={['auto', 'auto']}
                        width={42}
                      />
                      <Tooltip
                        formatter={(v, _name, ctx) => {
                          const p = ctx?.payload || {};
                          const reason = p.reason ? ` (${REASON_LABEL[p.reason] || p.reason}${p.delta ? ` ${p.delta > 0 ? '+' : ''}${p.delta}` : ''})` : '';
                          return [`${v}${reason}`, 'Qty'];
                        }}
                        labelFormatter={(v) => v ? new Date(v).toLocaleString() : ''}
                        contentStyle={{ fontSize: 12, padding: '6px 10px' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="qty"
                        stroke="hsl(var(--brand, 220 70% 50%))"
                        strokeWidth={2}
                        dot={{ r: 3, fill: 'hsl(var(--brand, 220 70% 50%))' }}
                        activeDot={{ r: 5 }}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Underlying events table */}
              {eventRows.length > 0 && (
                <div className="border border-border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted text-[10px] uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">When</th>
                        <th className="text-left px-3 py-2">Δ</th>
                        <th className="text-left px-3 py-2">Balance</th>
                        <th className="text-left px-3 py-2">Reason</th>
                        <th className="text-left px-3 py-2">Ref</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventRows.map((e, i) => (
                        <tr key={i} className="border-t border-border" data-testid={`stock-history-row-${i}`}>
                          <td className="px-3 py-2 text-muted-foreground text-xs whitespace-nowrap">
                            {e.ts ? new Date(e.ts).toLocaleString() : '—'}
                          </td>
                          <td className={`px-3 py-2 font-mono font-bold ${e.delta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {e.delta > 0 ? '+' : ''}{e.delta}
                          </td>
                          <td className="px-3 py-2 font-mono">{e.qty}</td>
                          <td className="px-3 py-2 text-xs uppercase tracking-wider">
                            <span className={`px-1.5 py-0.5 rounded ${REASON_COLOUR[e.reason] || 'bg-muted text-muted-foreground'}`}>
                              {REASON_LABEL[e.reason] || e.reason}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{e.ref || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
